import { motion } from "framer-motion";
import { MessageCircle, Phone, Mail, MapPin, Clock } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { CONTACT, WHATSAPP_URL, WHATSAPP_URL_EN, TEL_URL, ASSETS } from "../../lib/content";
import { SocialLinks } from "./SocialLinks";
import { useVisitors, formatIt } from "../../lib/visitors";
import { useLang } from "../../lib/i18n";

export const Contact = () => {
  const visitors = useVisitors();
  const { tr } = useLang();
  return (
    <footer
      id="contatti"
      data-testid="footer-section"
      className="relative bg-ad-black pt-24 md:pt-40 pb-12 overflow-hidden"
    >
      {/* Oversized ghost word */}
      <div className="pointer-events-none absolute -bottom-6 md:-bottom-10 left-0 right-0 select-none">
        <span className="block font-serif-display text-[22vw] leading-none text-white/[0.03] text-center tracking-tight">
          Divine
        </span>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <Reveal className="max-w-3xl">
          <Overline>{tr("Prenota il Tuo Momento", "Book Your Moment")}</Overline>
          <h2 className="mt-6 font-serif-display font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-white">
            {tr("Prenota il tuo", "Book your")} <br />
            <span className="italic text-gold">{tr("appuntamento", "appointment")}</span>
          </h2>
          <p className="mt-8 font-sans-ui font-light text-white/70 leading-relaxed max-w-xl text-base md:text-lg">
            {tr(
              "Scrivici su WhatsApp o chiamaci: fisseremo insieme una prova riservata in Atelier, dedicata solo a te.",
              "Message us on WhatsApp or call us: together we will arrange a private fitting at the Atelier, dedicated only to you."
            )}
          </p>
        </Reveal>

        {/* CTA buttons */}
        <Reveal delay={0.15}>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 max-w-2xl">
            <a
              data-testid="contact-whatsapp-cta"
              href={tr(WHATSAPP_URL, WHATSAPP_URL_EN)}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex-1 inline-flex items-center justify-center gap-3 bg-gold text-[#050505] px-8 py-5 font-sans-ui text-xs uppercase tracking-[0.25em] hover:bg-[#e6d3a3] transition-colors duration-400"
            >
              <MessageCircle size={18} />
              {tr("Scrivici su WhatsApp", "Message us on WhatsApp")}
            </a>
            <a
              data-testid="contact-phone-cta"
              href={TEL_URL}
              className="group flex-1 inline-flex items-center justify-center gap-3 border border-white/25 text-white px-8 py-5 font-sans-ui text-xs uppercase tracking-[0.25em] hover:border-gold hover:text-gold transition-colors duration-400"
            >
              <Phone size={17} />
              {tr("Chiama l'Atelier", "Call the Atelier")}
            </a>
          </div>
        </Reveal>

        {/* Contact grid */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 border-t border-white/12 pt-14">
          <Reveal data-testid="contact-address">
            <div className="flex items-center gap-2 text-gold mb-4">
              <MapPin size={15} />
              <span className="font-sans-ui text-[11px] uppercase tracking-[0.25em]">
                Atelier
              </span>
            </div>
            <p className="font-serif-display text-2xl text-white font-light">
              {CONTACT.company}
            </p>
            <p className="mt-2 font-sans-ui font-light text-white/60">
              {CONTACT.address}
            </p>
          </Reveal>

          <Reveal delay={0.1} data-testid="contact-phone-info">
            <div className="flex items-center gap-2 text-gold mb-4">
              <Phone size={15} />
              <span className="font-sans-ui text-[11px] uppercase tracking-[0.25em]">
                {tr("Telefono", "Phone")}
              </span>
            </div>
            <a
              href={TEL_URL}
              className="font-serif-display text-2xl text-white font-light hover:text-gold transition-colors duration-300"
            >
              {CONTACT.phoneDisplay}
            </a>
            <p className="mt-2 font-sans-ui font-light text-white/60">
              {tr("Disponibili anche su WhatsApp", "Also available on WhatsApp")}
            </p>
          </Reveal>

          <Reveal delay={0.2} data-testid="contact-email-info">
            <div className="flex items-center gap-2 text-gold mb-4">
              <Mail size={15} />
              <span className="font-sans-ui text-[11px] uppercase tracking-[0.25em]">
                Email
              </span>
            </div>
            <a
              href={`mailto:${CONTACT.emailCommerciale}`}
              className="block font-sans-ui font-light text-white hover:text-gold transition-colors duration-300"
            >
              {CONTACT.emailCommerciale}
            </a>
            <a
              href={`mailto:${CONTACT.emailDirezione}`}
              className="mt-1 block font-sans-ui font-light text-white/60 hover:text-gold transition-colors duration-300"
            >
              {CONTACT.emailDirezione}
            </a>
          </Reveal>

          <Reveal delay={0.3} data-testid="contact-hours-info">
            <div className="flex items-center gap-2 text-gold mb-4">
              <Clock size={15} />
              <span className="font-sans-ui text-[11px] uppercase tracking-[0.25em]">
                {tr("Orari", "Opening Hours")}
              </span>
            </div>
            <p className="font-serif-display text-2xl text-white font-light">
              {tr("Lun – Sab", "Mon – Sat")}
            </p>
            <p className="mt-2 font-sans-ui font-light text-white/60">
              9:30 – 13:00 · 16:00 – 20:00
            </p>
            <p className="mt-1 font-sans-ui font-light text-white/40">
              {tr("Domenica chiuso", "Sunday closed")}
            </p>
          </Reveal>
        </div>

        {/* Visitor counter */}
        <div
          data-testid="visitor-counter"
          className="relative mt-16 flex flex-col items-center gap-3 border-t border-white/12 pt-12"
        >
          <span className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-gold">
            {tr("Visitatori dell'Atelier", "Atelier Visitors")}
          </span>
          <span className="font-serif-display text-5xl md:text-6xl text-white tabular-nums leading-none">
            {visitors?.total != null ? formatIt(visitors.total) : "—"}
          </span>
          <span className="font-sans-ui text-[11px] text-white/40">
            {tr(
              "spose e sposi hanno già visitato questa pagina",
              "brides and grooms have already visited this page"
            )}
          </span>
        </div>

        {/* Bottom bar */}
        <div className="relative mt-16 flex flex-col md:flex-row items-center justify-between gap-6 text-white/40">
          <img
            src={ASSETS.logo}
            alt="Atelier Divine"
            className="h-16 w-16 aspect-square object-cover rounded-full shadow-lg shadow-black/40"
          />
          <div className="flex flex-col items-center md:items-end gap-4">
            <div className="flex items-center gap-4">
              <span className="font-sans-ui text-[11px] uppercase tracking-[0.25em] text-white/50">
                {tr("Seguici", "Follow us")}
              </span>
              <SocialLinks size={18} />
            </div>
            <span className="font-sans-ui text-[11px] tracking-wide text-center md:text-right">
              © {new Date().getFullYear()} {CONTACT.company} · {tr("Tutti i diritti riservati", "All rights reserved")}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};
